<template>
	<div v-if="loaded" class="container registration-form">
		<link
			rel="stylesheet"
			href="https://unpkg.com/vue-multiselect@2.1.0/dist/vue-multiselect.min.css"
		/>

		<h1>{{ formConfig.title }}</h1>

		<div class="mb-2 mt-2" v-html="formConfig.description" />

		<b-alert :show="!!errorMsg" variant="danger">{{ errorMsg }}</b-alert>

		<validation-observer v-show="state === 'Form'" v-slot="{ handleSubmit }">
			<b-form
				class="d-flex flex-column"
				@submit.prevent="handleSubmit(() => (state = 'Overview'))"
			>
				<b-row
					v-for="formModule in formConfig.fieldsConfiguration"
					:key="formModule.name"
					:class="'input-' + formModule.name + '-container'"
					class="mt-2"
				>
					<b-col>
						<div v-html="formModule.description" />

						<address-module
							v-if="formModule.name === 'Address'"
							:input="input"
							:form-config="formConfig"
							:form-module="formModule"
						/>

						<financial-details-module
							v-else-if="formModule.name === 'FinancialDetails'"
							:input="input"
							:form-config="formConfig"
							:form-module="formModule"
						/>

						<membership-module
							v-else-if="formModule.name === 'MembershipDetails'"
							:input="input"
							:form-config="formConfig"
							:form-module="formModule"
							@voucher-loaded="
								(data) => {
									voucherData = data;
								}
							"
						/>

						<services-module
							v-else-if="formModule.name === 'Services'"
							:input="input"
							:form-config="formConfig"
							:form-module="formModule"
						/>

						<generic-fields-module
							v-else-if="!formModule.isCustom"
							:input="input"
							:form-config="formConfig"
							:form-module="formModule"
						/>

						<parent-info-module
							v-else-if="formModule.isCustom && formModule.name === 'ParentInfo'"
							v-model="input.ParentInfos"
						/>

						<files-module
							v-else-if="formModule.isCustom && formModule.name === 'Files'"
							v-model="input.Files"
						/>
					</b-col>
				</b-row>

				<input type="hidden" v-model="input.validationToken" />

				<b-row>
					<b-col class="text-center mb-5">
						<hr />
						<b-btn type="submit" variant="primary" class="continue-button">
							{{ $t("continue") }}
						</b-btn>
					</b-col>
				</b-row>
			</b-form>
		</validation-observer>

		<b-modal
			id="personExistsWarningModal"
			static
			hide-header
			no-close-on-esc
			no-close-on-backdrop
		>
			<div v-html="formConfig.textToShowWhenPersonExists || $t('person_exists')" />
			<template #modal-footer="{ cancel }">
				<b-button variant="secondary" @click="cancel">
					{{ $t("ok") }}
				</b-button>
			</template>
		</b-modal>

		<confirmation-form
			v-if="state === 'Overview'"
			:voucherData="voucherData"
			:formInput="input"
			:formConfig="formConfig"
			@cancel="state = 'Form'"
		/>
	</div>
</template>

<script>
import axios from "axios";
import { appSettings } from "@/appSettings";
import { localize } from "vee-validate";
import moduleCommonMixin from "./moduleCommonMixin";
import { extend } from "vee-validate";

export default {
	mixins: [moduleCommonMixin],
	data() {
		return {
			state: "Form",
			formConfig: {},
			input: {},
			loaded: false,
			errorMsg: null,
			debounceTimer: null,
			voucherData: null,
		};
	},
	watch: {
		"input.FirstName"() {
			this.validateExistingPerson();
		},
		"input.LastName"() {
			this.validateExistingPerson();
		},
		"input.BirthDate"() {
			this.validateExistingPerson();
		},
		"input.Gender"() {
			this.validateExistingPerson();
		},
	},
	async created() {
		var dateTimeNow = new Date().toISOString();
		this.input.validationToken = Buffer.from(dateTimeNow).toString("base64");

		await this.loadFormConfig();

		if (this.errorMsg || !this.formConfig.fieldsConfiguration) {
			return;
		}

		let me = this;

		let passwordField = this.allFields.find((x) => x.key == "Password");
		let confirmPasswordField = this.allFields.find(
			(x) => x.key == "ConfirmPassword"
		);

		if (passwordField && confirmPasswordField) {
			let passwordFieldLabel = passwordField.customLabel ?? passwordField.label;
			let confirmPasswordFieldLabel =
				confirmPasswordField.customLabel ?? confirmPasswordField.label;
			extend("confirmPassword", {
				validate(value) {
					return value === me.input.Password;
				},
				message: me.$t(`rules.must_be_the_same`, {
					field1: confirmPasswordFieldLabel,
					field2: passwordFieldLabel,
				}),
			});
		}

		let emailField = this.allFields.find((x) => x.type == "Email");
		let confirmEmailField = this.allFields.find((x) => x.key == "ConfirmEmail");

		if (emailField && confirmEmailField) {
			let emailFieldLabel = emailField.customLabel ?? emailField.label;
			let confirmEmailFieldLabel =
				confirmEmailField.customLabel ?? confirmEmailField.label;
			extend("confirmEmail", {
				validate(value) {
					let emailVal = me.input.Email;

					if (emailVal && typeof emailVal === "object") {
						if (emailVal.Email !== undefined) {
							emailVal = emailVal.Email;
						} else if (emailVal.value !== undefined) {
							emailVal = emailVal.value;
						} else if (emailVal.target && emailVal.target.value !== undefined) {
							emailVal = emailVal.target.value;
						}
					}

					const v1 = String(value || "")
						.trim()
						.toLowerCase();
					const v2 = String(emailVal || "")
						.trim()
						.toLowerCase();

					return v1 === v2;
				},
				message: me.$t(`rules.must_be_the_same`, {
					field1: confirmEmailFieldLabel,
					field2: emailFieldLabel,
				}),
			});
		}
	},
	methods: {
		async validateExistingPerson() {
			var firstName = this.input.FirstName;
			var lastName = this.input.LastName;
			var birthDate = this.input.BirthDate;
			var gender = this.input.Gender;

			if (firstName && lastName && birthDate && (gender || gender == 0)) {
				var model = {
					firstName,
					lastName,
					birthDate,
					gender,
					organisationId: this.formConfig.tenantId,
				};

				clearTimeout(this.debounceTimer);
				this.debounceTimer = setTimeout(() => {
					var existingPersonCheckUrl = `${appSettings.FoysPubRegistrationsUrl}/members/existing-person`;

					axios
						.get(existingPersonCheckUrl, {
							params: model,
						})
						.then(() => {
							this.$bvModal.show("personExistsWarningModal");
						});
				}, 500);
			}
		},
		getUrl() {
			let registrationFormEntry = document.querySelectorAll(
				"registration-form-entry"
			)[0];
			if (typeof registrationFormEntry === "undefined") {
				throw new Error('"registration-form-entry" html element is not found');
			}
			let configId = registrationFormEntry.getAttribute("configuration");
			if (!configId) {
				throw new Error(
					'"registration-form-entry" configuration property is not set'
				);
			}

			return `${appSettings.FoysPubRegistrationsUrl}/registration-forms/${configId}`;
		},
		async loadFormConfig() {
			await axios
				.get(this.getUrl())
				.then((response) => {
					this.formConfig = response.data;
					this.$i18n.locale = this.formConfig.locale;
					localize(this.formConfig.locale.substring(0, 2));

					for (var formModule of this.formConfig.fieldsConfiguration.filter(
						(x) => !x.isCustom
					)) {
						formModule.fields.forEach((x) => this.$set(x, "isVisible", true));
					}

					this.loaded = true;
				})
				.catch((error) => {
					this.errorMsg = error;
					console.log(error);
				});
		},
	},
};
</script>
